import React, { forwardRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Autocomplete as MuiAutocomplete,
  CircularProgress,
} from "@mui/material";
import { BottomArrowIcon } from "../Icons";
import { Label, StyledTextField, IconContainer } from "./Autocomplete.styled";

export interface AutocompleteOption {
  value: string | number;
  label: string;
}

interface AutocompleteProps {
  label?: string;
  placeholder?: string;
  value?: AutocompleteOption | null;
  onChange?: (value: AutocompleteOption | null) => void;
  onInputChange?: (value: string) => void;
  disabled?: boolean;
  fullWidth?: boolean;
  width?: string | number;
  name?: string;
  id?: string;
  error?: boolean;
  helperText?: string;
  required?: boolean;
  options: AutocompleteOption[];
  loading?: boolean;
  noOptionsText?: string;
}

const Autocomplete = forwardRef<HTMLDivElement, AutocompleteProps>(
  (
    {
      label,
      placeholder,
      value,
      onChange,
      onInputChange,
      disabled = false,
      fullWidth = false,
      width,
      name,
      id,
      error = false,
      helperText,
      required = false,
      options = [],
      loading = false,
      noOptionsText,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const [inputValue, setInputValue] = useState("");
    const defaultNoOptionsText = noOptionsText || t("select.noOptions");

    const handleChange = (
      _event: React.SyntheticEvent,
      newValue: AutocompleteOption | null,
    ) => {
      onChange?.(newValue);
    };

    const handleInputChange = (
      _event: React.SyntheticEvent,
      newInputValue: string,
    ) => {
      setInputValue(newInputValue);
      onInputChange?.(newInputValue);
    };

    return (
      <Box sx={{ width: fullWidth ? "100%" : width }} ref={ref}>
        {label && (
          <Label>
            {label}
            {required && (
              <Box component="span" sx={{ color: "inherit" }}>
                {" "}
                *
              </Box>
            )}
          </Label>
        )}
        <MuiAutocomplete
          value={value}
          onChange={handleChange}
          inputValue={inputValue}
          onInputChange={handleInputChange}
          options={options}
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, val) => option.value === val.value}
          disabled={disabled}
          loading={loading}
          noOptionsText={defaultNoOptionsText}
          popupIcon={
            <IconContainer>
              <BottomArrowIcon width="16px" height="16px" />
            </IconContainer>
          }
          renderInput={(params) => (
            <StyledTextField
              {...params}
              placeholder={placeholder}
              name={name}
              id={id}
              error={error}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        {helperText && (
          <Typography
            variant="caption"
            sx={{
              color: error ? "status.error" : "text.secondary",
              fontSize: "12px",
              mt: 0.5,
              display: "block",
            }}
          >
            {helperText}
          </Typography>
        )}
      </Box>
    );
  },
);

Autocomplete.displayName = "Autocomplete";

export default Autocomplete;
